import React, { useEffect, useState } from "react";
import "./Footer.scss";
import PropTypes from "prop-types";
import useWindowLocation from "../hooks/useWindowLocation";
import useScrollPos from "../hooks/useScrollPos";
import PageLink from "./share/PageLink/PageLink";

import { graphql, StaticQuery } from "gatsby";

import Container from "./share/Container/Container";

interface IFooterProps {
  data: any;
}

const Footer: React.FC<IFooterProps> = ({ data }) => {
  const {
    adress,
    numbers,
    openinghours,
    webadress,
  } = data.markdownRemark.frontmatter.contactblock;

  const location = useWindowLocation().pathname;
  const scrollPos = useScrollPos();
  const [hasMap, setHasMap] = useState(false);
  // Der Footer ist jetzt so hoch wie sein Inhalt. Die Variante
  // "half-height--bottom" streckte ihn auf halbe Viewporthoehe und hinterliess
  // darunter eine grosse leere Flaeche.
  const variant = ["no-top", "padding-s"];

  useEffect(() => {
    setHasMap(location.includes("/about") || location.includes("/contact"));
  }, [location]);

  useEffect(() => {
    const hash = window.location.hash;
    if (hash !== "") {
      const id = hash.replace("#", "");
      const element = document.getElementById(id);
      if (element && element.scrollIntoView) {
        element.scrollIntoView(false);
      }
    }
  }, []);

  // Leerzeichen raus, sonst waehlt "tel:0511 2713145" auf manchen Geraeten nicht.
  const newPhone = numbers.phone.replace(/\s/g, "");

  return (
    <footer className={`Footer${hasMap ? " Footer--full-height" : ""}`} id="contact">
      <Container variant={variant}>
        <h2>Kontakt</h2>
        <div className="Footer__content">
          <div className="Footer__col">
            <h5 className="Footer__label">Öffnungszeiten</h5>
            <div className="Footer__hours">
              {openinghours.map((element, index) => {
                const { daysbegin, daysend, timebegin, timeend } = element;
                return (
                  <div className="Footer__hoursRow" key={index}>
                    <span className="Footer__hoursDays">
                      {daysbegin === daysend
                        ? daysbegin
                        : `${daysbegin} – ${daysend}`}
                    </span>
                    <span className="Footer__hoursTime">
                      {timebegin} – {timeend} Uhr
                    </span>
                  </div>
                );
              })}
            </div>
            {openinghours
              .filter((element) => element.specialinfo)
              .map((element, index) => (
                <p className="Footer__note" key={index}>
                  {element.specialinfo}
                </p>
              ))}
          </div>
          <div className="Footer__col">
            <div className="Footer__group">
              <h5 className="Footer__label">E-Mail</h5>
              <a className="Footer__value" href={"mailto:" + webadress.mail}>
                {webadress.mail}
              </a>
            </div>
            <div className="Footer__group">
              <h5 className="Footer__label">Telefon</h5>
              <a className="Footer__value" href={"tel:" + newPhone}>
                {numbers.phone}
              </a>
            </div>
          </div>
          <div className="Footer__col">
            <h5 className="Footer__label">Adresse</h5>
            <p className="Footer__value">
              {adress.name}
              <br />
              {adress.street}
              <br />
              {adress.city}
            </p>
          </div>
        </div>
        {hasMap && (
          <div className="Footer__map">
            <iframe
              title="Standort Heinze Pool GmbH"
              src="https://www.openstreetmap.org/export/embed.html?bbox=9.8301%2C52.4210%2C9.8421%2C52.4290&layer=mapnik&marker=52.4250%2C9.8361"
              width="100%"
              height="450"
              loading="lazy"
            />
            <a
              href="https://www.google.de/maps/dir//Siemensstra%C3%9Fe+3,+30916+Isernhagen"
              target="_blank"
              rel="noopener noreferrer"
              className="Footer__mapLink"
            >
              Anfahrt planen
            </a>
          </div>
        )}
        <nav className="Footer__areas" aria-label="Einzugsgebiet">
          <h5 className="Footer__label">Poolbau in Ihrer Region</h5>
          <ul>
            <li><PageLink to="/poolbau">Hannover</PageLink></li>
            <li><PageLink to="/regionen/poolbau-niedersachsen">Niedersachsen</PageLink></li>
            <li><PageLink to="/regionen/poolbau-langenhagen">Langenhagen</PageLink></li>
            <li><PageLink to="/regionen/poolbau-burgwedel">Burgwedel</PageLink></li>
            <li><PageLink to="/regionen/poolbau-lehrte">Lehrte</PageLink></li>
            <li><PageLink to="/regionen/poolbau-garbsen">Garbsen</PageLink></li>
            <li><PageLink to="/regionen/poolbau-wedemark">Wedemark</PageLink></li>
            <li><PageLink to="/regionen/poolbau-celle">Celle</PageLink></li>
            <li><PageLink to="/regionen/poolbau-hildesheim">Hildesheim</PageLink></li>
          </ul>
        </nav>
        <div className="Footer__subline">
          <PageLink to="/karriere">
            Karriere
          </PageLink>
          <PageLink to="/faq">
            FAQ
          </PageLink>
          <PageLink to="/meta/impressum">
            Impressum & Datenschutz
          </PageLink>
        </div>
      </Container>
    </footer>
  );
};

Footer.propTypes = {
  data: PropTypes.object,
};

export default () => (
  <StaticQuery
    query={graphql`
      query FooterQuery {
        markdownRemark(frontmatter: { templateKey: { eq: "about-page" } }) {
          frontmatter {
            contactblock {
              adress {
                name
                city
                street
              }
              numbers {
                fax
                phone
              }
              openinghours {
                daysbegin
                daysend
                timebegin
                timeend
                specialinfo
              }
              webadress {
                mail
                web
              }
            }
          }
        }
      }
    `}
    render={(data) => <Footer data={data} />}
  />
);
